const logo = "/bluedottedline-logo.png";

const connectPathnames = new Set(["/connect", "/connect/"]);

const proofPoints = [
  { label: "Answer", copy: "Route calls to the right staff path, a polished AI receptionist, or both when your team is busy, closed, or focused with a client." },
  { label: "Capture", copy: "Collect caller intent, contact details, and follow-up notes without pretending the system can book what it cannot." },
  { label: "Text", copy: "Send consent-aware transactional texts for sign-in codes, booking links, reminders, and customer-care follow-up." },
] as const;

const routingModes = [
  { title: "Simultaneous ringing", copy: "Ring multiple staff phones at once when the first available person should pick up." },
  { title: "Hunt or sequential routing", copy: "Try a preferred order of team members before sending the caller to AI coverage or voicemail." },
  { title: "Date and time rules", copy: "Use business hours, after-hours windows, holidays, or campaign-specific schedules to decide where calls land." },
] as const;

const capabilities = [
  "Human-friendly AI call handling",
  "Business-specific knowledge",
  "Call summaries and follow-up notes",
  "Simultaneous, hunt, and date/time call routing",
  "Telnyx and Twilio provider support",
  "SMS opt-in, STOP, and HELP handling",
  "Staff phone and work alert controls",
] as const;

const chatGptConnectorTasks = [
  "Review recent conversations, missed calls, and follow-up status",
  "Add timestamped customer notes from an authorized tenant-admin account",
  "Switch between authorized organizations or locations",
  "Inspect and adjust AI SMS settings with confirmation",
] as const;

export const isConnectPromotionPath = (pathname: string): boolean => connectPathnames.has(pathname);

function ArrowIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" /></svg>;
}

function CheckIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>;
}

export function ConnectPromotionPage() {
  return <main className="connect-page">
    <nav className="connect-nav" aria-label="Connect promotion navigation">
      <a className="connect-brand" href="/" aria-label="Blue Dotted Line home"><img src={logo} alt="Blue Dotted Line" /></a>
      <div>
        <a href="/">Forms</a>
        <a href="#connect-capabilities">Capabilities</a>
        <a href="#connect-fit">Best fit</a>
        <a className="connect-nav-cta" href="mailto:hello@bluedottedline.com?subject=Blue%20Dotted%20Line%20Connect">Talk to us</a>
      </div>
    </nav>

    <section className="connect-hero" aria-labelledby="connect-title">
      <div className="connect-hero-copy">
        <p className="connect-kicker">Blue Dotted Line Connect</p>
        <h1 id="connect-title">AI phone and messaging support for service businesses.</h1>
        <p>Connect helps small teams answer more calls, route callers by schedule or staff availability, capture useful follow-up details, and send consent-aware texts while keeping bookings, payments, and customer decisions under human control.</p>
        <div className="connect-actions">
          <a className="landing-button" href="mailto:hello@bluedottedline.com?subject=Blue%20Dotted%20Line%20Connect%20demo">Request Demo <ArrowIcon /></a>
          <a className="landing-secondary-button" href="#connect-fit">See Where It Fits</a>
        </div>
      </div>
      <div className="connect-console" aria-label="Connect call handling preview">
        <div className="connect-console-top"><span /> Live call assist</div>
        <div className="connect-call-card">
          <span>Incoming caller</span>
          <strong>New client asking about availability</strong>
          <p>AI receptionist explains services, captures preferred times, and creates a clear follow-up note for staff.</p>
        </div>
        <div className="connect-signal-grid">
          <span>Voice</span>
          <span>SMS</span>
          <span>Routing</span>
          <span>Follow-up</span>
        </div>
      </div>
    </section>

    <section className="connect-strip" aria-label="Connect operating promise">
      <strong>Designed for the gap between missed calls and a full booking platform.</strong>
      <span>Use Connect when you want better front-desk coverage without handing over every operational decision to automation.</span>
    </section>

    <section className="connect-proof" id="connect-capabilities" aria-labelledby="connect-capabilities-title">
      <header>
        <p className="connect-kicker">What Connect Does</p>
        <h2 id="connect-capabilities-title">Front-desk coverage with clear boundaries.</h2>
      </header>
      <div className="connect-proof-grid">{proofPoints.map((item) => <article key={item.label}><span>{item.label}</span><p>{item.copy}</p></article>)}</div>
    </section>

    <section className="connect-routing" aria-labelledby="connect-routing-title">
      <div>
        <p className="connect-kicker">Routing Flow</p>
        <h2 id="connect-routing-title">Route calls by team, timing, and urgency.</h2>
        <p>Connect can support front-desk flows where calls ring everyone at once, hunt through staff in order, or change behavior by date, time, and business-hours rules before falling back to AI assistance.</p>
      </div>
      <div className="connect-routing-grid">{routingModes.map((item) => <article key={item.title}><h3>{item.title}</h3><p>{item.copy}</p></article>)}</div>
    </section>

    <section className="connect-fit" id="connect-fit" aria-labelledby="connect-fit-title">
      <div>
        <p className="connect-kicker">Best Fit</p>
        <h2 id="connect-fit-title">Built for teams that lose revenue when the phone goes unanswered.</h2>
        <p>Spas, wellness clinics, salons, medspas, pet services, and local service businesses can use Connect to cover routine conversations, collect structured details, and move the next step back to the right person.</p>
      </div>
      <ul>{capabilities.map((item) => <li key={item}><CheckIcon /> {item}</li>)}</ul>
    </section>

    <section className="connect-chatgpt" aria-labelledby="connect-chatgpt-title">
      <div>
        <p className="connect-kicker">ChatGPT Connector</p>
        <h2 id="connect-chatgpt-title">Ask about your front desk from ChatGPT.</h2>
        <p>Connect is preparing a ChatGPT connector for authorized tenant admins and owners. After signing in with their normal Connect account, supported ChatGPT users can inspect customer conversations, calls, notes, AI SMS settings, and multiple locations without needing developer or AWS access.</p>
        <small>Availability depends on supported ChatGPT plans, workspace settings, and authorized Connect access.</small>
      </div>
      <ul>{chatGptConnectorTasks.map((item) => <li key={item}><CheckIcon /> {item}</li>)}</ul>
    </section>

    <section className="connect-final" aria-labelledby="connect-final-title">
      <p className="connect-kicker">A Practical Pilot</p>
      <h2 id="connect-final-title">Add a smarter front door before you rebuild the whole house.</h2>
      <p>Connect is best introduced as a measured communications layer: one number, one use case, clear opt-in language, and staff review before expanding automation.</p>
      <a className="landing-button landing-button-light" href="mailto:hello@bluedottedline.com?subject=Blue%20Dotted%20Line%20Connect">Start the Conversation <ArrowIcon /></a>
    </section>
  </main>;
}
